import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaTruck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yardVehicleService } from '../../../../services/yardVehicleService';
import { formatIndianRegistration } from '../../../../utils/indianRegistration';
import YardVehicleEntryForm from './YardVehicleEntryForm';

function StatusBadge({ vehicle }) {
  if (vehicle.fraudRisk || vehicle.adminReviewRequired) {
    return (
      <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-semibold">
        Admin review
      </span>
    );
  }
  if (vehicle.spotCheckRequired) {
    return (
      <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">
        Spot check
      </span>
    );
  }
  if (vehicle.vahanVerified) {
    return (
      <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold">
        Vahan verified
      </span>
    );
  }
  return null;
}

export default function YardVehicles() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const list = await yardVehicleService.list();
      setVehicles(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Failed to load yard vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleSuccess = (vehicle) => {
    setShowForm(false);
    loadVehicles();
    if (vehicle?.vehicleId) {
      navigate(`/vendor/dashboard/yard/${vehicle.vehicleId}/parts`);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Yard vehicles</h2>
          <p className="text-sm text-slate-500 mt-1">
            Add vehicles first — Sparepe generates parts from each vehicle node (Mode A/B).
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
          >
            <FaPlus /> Add vehicle
          </button>
        )}
      </div>

      {showForm ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <YardVehicleEntryForm
            onSuccess={handleSuccess}
            onCancel={() => setShowForm(false)}
          />
        </div>
      ) : (
        <>
          {loading ? (
            <p className="text-sm text-slate-500">Loading vehicles…</p>
          ) : vehicles.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
              <FaTruck className="mx-auto text-4xl text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-700">No vehicles on yard yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Start with a vehicle photo and registration — parts are generated from each vehicle
                node.
              </p>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
              >
                <FaPlus /> Add first vehicle
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {vehicles.map((v) => (
                <div
                  key={v.vehicleId}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/vendor/dashboard/yard/${v.vehicleId}/parts`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/vendor/dashboard/yard/${v.vehicleId}/parts`);
                  }}
                  className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer"
                >
                  {v.photoUrl && (
                    <img
                      src={v.photoUrl}
                      alt={`${v.make} ${v.model}`}
                      className="w-full h-36 object-cover"
                    />
                  )}
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-slate-900">
                        {v.make} {v.model} ({v.year})
                      </h3>
                      <StatusBadge vehicle={v} />
                    </div>
                    <p className="text-xs font-mono text-slate-500">
                      {formatIndianRegistration(v.registrationNumber)}
                    </p>
                    <p className="text-xs text-slate-500 capitalize">{v.status}</p>
                    <p className="text-xs font-semibold text-emerald-600 pt-1">Price parts →</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
